<script setup lang="ts">
import { useId } from 'vue'

defineProps<{
  active: boolean
  reducedMotion: boolean
  compact?: boolean
}>()

const sceneId = useId().replaceAll(':', '')

function paint(name: string) {
  return `url(#${sceneId}-${name})`
}
</script>

<template>
  <!-- 分期支付的小故事：银行卡触发一笔账单，拆成清晰的支付卡片与时间轴。 -->
  <div
    class="financing-scene relative size-full"
    :class="{ 'is-active': active, 'is-reduced': reducedMotion, 'is-compact': compact }"
    aria-hidden="true"
  >
    <svg class="size-full overflow-visible" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient :id="`${sceneId}-stage`" x1="42" y1="44" x2="356" y2="356" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity=".1" />
          <stop offset=".55" stop-color="#C4B5FD" stop-opacity=".035" />
          <stop offset="1" stop-color="#2E1065" stop-opacity=".2" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-card-left`" x1="50" y1="178" x2="175" y2="294" gradientUnits="userSpaceOnUse">
          <stop stop-color="#DDD6FE" stop-opacity=".5" />
          <stop offset=".48" stop-color="#A78BFA" stop-opacity=".27" />
          <stop offset="1" stop-color="#4C1D95" stop-opacity=".55" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-card-center`" x1="139" y1="165" x2="268" y2="285" gradientUnits="userSpaceOnUse">
          <stop stop-color="#F5F3FF" stop-opacity=".58" />
          <stop offset=".46" stop-color="#C4B5FD" stop-opacity=".34" />
          <stop offset="1" stop-color="#5B21B6" stop-opacity=".56" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-card-right`" x1="232" y1="180" x2="356" y2="294" gradientUnits="userSpaceOnUse">
          <stop stop-color="#E9D5FF" stop-opacity=".48" />
          <stop offset=".5" stop-color="#C084FC" stop-opacity=".26" />
          <stop offset="1" stop-color="#4C1D95" stop-opacity=".56" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-chip`" x1="0" y1="0" x2="28" y2="24" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FEF3C7" />
          <stop offset=".45" stop-color="#FCD34D" />
          <stop offset="1" stop-color="#D97706" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-path`" x1="62" y1="0" x2="338" y2="0" gradientUnits="userSpaceOnUse">
          <stop stop-color="#C4B5FD" stop-opacity=".12" />
          <stop offset=".5" stop-color="#A7F3D0" stop-opacity=".8" />
          <stop offset="1" stop-color="#A7F3D0" stop-opacity=".24" />
        </linearGradient>
      </defs>

      <g class="stage-frame">
        <rect x="28" y="42" width="340" height="316" rx="44" :fill="paint('stage')" stroke="white" stroke-opacity=".17" stroke-width="1.5" />
        <path d="M56 76H92M304 76H340M56 324H92M304 324H340" stroke="#C4B5FD" stroke-opacity=".22" stroke-width="2" stroke-linecap="round" />
        <circle cx="52" cy="99" r="2.5" fill="#F9A8D4" fill-opacity=".35" />
        <circle cx="344" cy="300" r="2.5" fill="#A7F3D0" fill-opacity=".35" />
      </g>

      <g class="purchase-origin">
        <rect x="137" y="105" width="126" height="164" rx="24" fill="#7C3AED" fill-opacity=".36" stroke="#EDE9FE" stroke-opacity=".5" stroke-width="2" />
        <rect x="148" y="117" width="104" height="140" rx="17" fill="#2E1065" fill-opacity=".26" stroke="white" stroke-opacity=".1" />
        <path d="M168 144H232M168 158H219M168 190H232M168 206H226M168 222H208" stroke="#EDE9FE" stroke-opacity=".45" stroke-width="4" stroke-linecap="round" />
        <rect x="165" y="238" width="70" height="8" rx="4" fill="#F9A8D4" fill-opacity=".7" />
        <path d="M229 130L239 140L229 150" stroke="#F9A8D4" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
      </g>

      <circle class="purchase-pulse" cx="267" cy="185" r="34" stroke="#FBCFE8" stroke-width="2" />

      <g class="installment-stack">
        <g class="installment installment-one">
          <rect x="42" y="198" width="128" height="84" rx="21" :fill="paint('card-left')" stroke="#EDE9FE" stroke-opacity=".46" stroke-width="2" />
          <rect x="55" y="211" width="102" height="58" rx="13" stroke="white" stroke-opacity=".1" />
          <g class="chip fine-detail">
            <rect x="61" y="221" width="27" height="22" rx="6" :fill="paint('chip')" />
            <path d="M70 221V243M79 221V243M61 232H88" stroke="#92400E" stroke-opacity=".45" stroke-width="1" />
          </g>
          <path d="M99 225H145M99 238H134M61 258H78M84 258H99M105 258H120" stroke="#EDE9FE" stroke-opacity=".38" stroke-width="3" stroke-linecap="round" />
          <path d="M54 211C84 196 122 199 153 211L54 261V211Z" fill="white" fill-opacity=".05" />
        </g>

        <g class="installment installment-two">
          <rect x="136" y="176" width="128" height="84" rx="21" :fill="paint('card-center')" stroke="#F5F3FF" stroke-opacity=".62" stroke-width="2.2" />
          <rect x="149" y="189" width="102" height="58" rx="13" stroke="white" stroke-opacity=".13" />
          <g class="chip fine-detail">
            <rect x="155" y="199" width="27" height="22" rx="6" :fill="paint('chip')" />
            <path d="M164 199V221M173 199V221M155 210H182" stroke="#92400E" stroke-opacity=".45" stroke-width="1" />
          </g>
          <path d="M193 203H239M193 216H228M155 236H172M178 236H193M199 236H214" stroke="#F5F3FF" stroke-opacity=".46" stroke-width="3" stroke-linecap="round" />
          <circle cx="238" cy="236" r="5" fill="#A7F3D0" fill-opacity=".72" />
          <path d="M148 189C178 174 216 177 247 189L148 239V189Z" fill="white" fill-opacity=".07" />
        </g>

        <g class="installment installment-three">
          <rect x="230" y="198" width="128" height="84" rx="21" :fill="paint('card-right')" stroke="#E9D5FF" stroke-opacity=".44" stroke-width="2" />
          <rect x="243" y="211" width="102" height="58" rx="13" stroke="white" stroke-opacity=".1" />
          <g class="chip fine-detail">
            <rect x="249" y="221" width="27" height="22" rx="6" :fill="paint('chip')" />
            <path d="M258 221V243M267 221V243M249 232H276" stroke="#92400E" stroke-opacity=".45" stroke-width="1" />
          </g>
          <path d="M287 225H333M287 238H322M249 258H266M272 258H287M293 258H308" stroke="#E9D5FF" stroke-opacity=".38" stroke-width="3" stroke-linecap="round" />
          <path d="M242 211C272 196 310 199 341 211L242 261V211Z" fill="white" fill-opacity=".05" />
        </g>
      </g>

      <g class="payment-path">
        <path d="M64 307C116 291 162 321 207 307C252 293 289 305 333 296" :stroke="paint('path')" stroke-width="3" stroke-linecap="round" />
        <circle cx="64" cy="307" r="4" fill="#C4B5FD" fill-opacity=".5" />
        <circle cx="207" cy="307" r="4" fill="#A7F3D0" fill-opacity=".66" />
        <g class="path-end">
          <circle cx="336" cy="295" r="18" fill="#A7F3D0" />
          <rect x="328" y="288" width="16" height="14" rx="4" stroke="#2E1065" stroke-width="2" />
          <path d="M328 292H344M332 285V290M340 285V290" stroke="#2E1065" stroke-width="2" stroke-linecap="round" />
          <path d="M334 297L337 300L342 295" stroke="#2E1065" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </g>
        <circle class="path-light" cx="64" cy="307" r="7" fill="#ECFDF5" />
      </g>

      <g class="card-badge">
        <rect x="294" y="35" width="90" height="90" rx="27" fill="#F9A8D4" />
        <rect x="300" y="41" width="78" height="78" rx="22" stroke="white" stroke-opacity=".34" />
        <rect x="317" y="61" width="44" height="35" rx="9" fill="#2E1065" fill-opacity=".06" stroke="#24100A" stroke-width="4" />
        <path d="M318 72H360" stroke="#24100A" stroke-width="4" />
        <rect x="323" y="82" width="14" height="5" rx="2.5" fill="#24100A" fill-opacity=".32" />
        <circle cx="367" cy="52" r="3" fill="white" fill-opacity=".64" />
        <path d="M307 56L314 49" stroke="white" stroke-opacity=".56" stroke-width="2" stroke-linecap="round" />
      </g>
    </svg>
  </div>
</template>

<style scoped>
.stage-frame,
.purchase-origin,
.installment,
.card-badge {
  transform-box: fill-box;
  transform-origin: center;
}

.installment-one {
  transform: rotate(-3deg);
}

.purchase-origin,
.installment-stack,
.card-badge,
.path-end {
  filter: drop-shadow(0 10px 10px rgb(30 9 59 / 26%));
}

.installment-three {
  transform: rotate(3deg);
}

.purchase-origin,
.purchase-pulse,
.path-light {
  opacity: 0;
}

.is-active .stage-frame {
  animation: financing-stage-in 600ms cubic-bezier(.22, 1, .36, 1) 350ms both;
}

.is-active .purchase-origin {
  animation: financing-origin 1.05s cubic-bezier(.22, 1, .36, 1) 420ms both;
}

.is-active .card-badge {
  animation: financing-card-in 520ms cubic-bezier(.34, 1.2, .64, 1) 690ms both;
}

.is-active .purchase-pulse {
  animation: financing-contact 500ms ease-out 810ms both;
}

.is-active .installment-one {
  animation: financing-one-in 600ms cubic-bezier(.22, 1, .36, 1) 1.06s both;
}

.is-active .installment-two {
  animation: financing-two-in 600ms cubic-bezier(.22, 1, .36, 1) 1.14s both;
}

.is-active .installment-three {
  animation: financing-three-in 600ms cubic-bezier(.22, 1, .36, 1) 1.22s both;
}

.is-active .payment-path {
  animation: financing-path-in 420ms ease-out 1.43s both;
}

.is-active .path-light {
  animation: financing-path-light 650ms ease-in-out 1.48s both;
}

@keyframes financing-stage-in {
  from { opacity: 0; transform: scale(.96); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes financing-origin {
  0% { opacity: 0; transform: translateY(12px) scale(.92); }
  22%, 62% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(8px) scale(.84); }
}

@keyframes financing-card-in {
  from { opacity: 0; transform: translate(-70px, 104px) rotate(-5deg) scale(.86); }
  72% { opacity: 1; transform: translate(2px, -3px) rotate(1deg) scale(1.03); }
  to { opacity: 1; transform: translate(0) rotate(0) scale(1); }
}

@keyframes financing-contact {
  from { opacity: .78; transform: scale(.42); }
  to { opacity: 0; transform: scale(1.5); }
}

@keyframes financing-one-in {
  from { opacity: 0; transform: translate(98px, -18px) rotate(0) scale(.88); }
  to { opacity: 1; transform: translate(0) rotate(-3deg) scale(1); }
}

@keyframes financing-two-in {
  from { opacity: 0; transform: translateY(-18px) scale(.88); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes financing-three-in {
  from { opacity: 0; transform: translate(-98px, -18px) rotate(0) scale(.88); }
  to { opacity: 1; transform: translate(0) rotate(3deg) scale(1); }
}

@keyframes financing-path-in {
  from { opacity: 0; transform: scaleX(.2); transform-origin: left; }
  to { opacity: 1; transform: scaleX(1); transform-origin: left; }
}

@keyframes financing-path-light {
  from { opacity: 0; transform: translateX(0); }
  30%, 70% { opacity: 1; }
  to { opacity: 0; transform: translateX(270px); }
}

.is-compact .fine-detail,
.is-compact .purchase-origin path:nth-of-type(n+2) {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .financing-scene *,
  .financing-scene *::before,
  .financing-scene *::after {
    animation: none !important;
  }

  .purchase-origin,
  .purchase-pulse,
  .path-light {
    display: none;
  }
}

.is-reduced *,
.is-reduced *::before,
.is-reduced *::after {
  animation: none !important;
}

.is-reduced .purchase-origin,
.is-reduced .purchase-pulse,
.is-reduced .path-light {
  display: none;
}
</style>
